import { useState, useEffect } from 'react'

export function useLocalStorage<T>(key: string, initialValue: T): [T, (value: T) => void] {
  // Get from local storage then parse stored json or return initialValue
  const readValue = (): T => {
    if (typeof window === 'undefined') {
      return initialValue
    }

    try {
      const item = window.localStorage.getItem(key)
      return item ? (JSON.parse(item) as T) : initialValue
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error)
      return initialValue
    }
  }

  // State to store our value
  const [storedValue, setStoredValue] = useState<T>(readValue)

  // Return a wrapped version of useState's setter function that persists the new value to localStorage
  const setValue = (value: T) => {
    if (typeof window === 'undefined') {
      console.warn(`Cannot set localStorage key "${key}" when window is undefined`)
      return
    }

    try {
      // Allow value to be a function so we have the same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value
      
      // Save to state
      setStoredValue(valueToStore)
      
      // Save to local storage
      window.localStorage.setItem(key, JSON.stringify(valueToStore))
      
      // Dispatch a custom event so other hooks can update
      window.dispatchEvent(new Event('local-storage'))
    } catch (error) {
      console.warn(`Error setting localStorage key "${key}":`, error)
    }
  }
  
  // Listen for changes to this localStorage key in other documents/tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key && e.newValue) {
        setStoredValue(JSON.parse(e.newValue))
      }
    }
    
    const handleLocalChange = () => {
      setStoredValue(readValue())
    }
    
    // Listen for storage events (other tabs/windows)
    window.addEventListener('storage', handleStorageChange)
    // Listen for local change events (same document)
    window.addEventListener('local-storage', handleLocalChange)
    
    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('local-storage', handleLocalChange)
    }
  }, [key])

  return [storedValue, setValue]
} 